import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { history } from "../../redux/store/configureStore";
import { checkToken } from "../../redux/actions/usersActions";
import { SIGN_OUT } from "../../redux/actions/actionTypes";
import { getStoredAuthIdentity, getStoredAuthSession, getStoredAuthToken, subscribeAuthTokenChanges } from "../../services/authTokenStorage";
import { AUTH_RECOVERY_EVENT, SESSION_WARNING_EVENT, getPendingLogout, getSessionWarning, refreshAccessToken, reportUserActivity, retryPendingLogout } from "../../services/sessionCoordinator";

export default function SessionManager() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [warning, setWarning] = useState(getSessionWarning());
  useEffect(() => {
    let identity;
    let token;
    let timer;
    let redirectTimer;
    let mounted = true;
    const verifyDeadline = () => {
      clearTimeout(timer);
      const session = getStoredAuthSession();
      if (!session || !getStoredAuthToken()) return;
      const delay = Math.max(1000, Math.min(2147483647, session.idleExpiresAt - Date.now()));
      timer = setTimeout(() => {
        refreshAccessToken().catch(() => undefined).finally(() => {
          // API decides expiry. Offline operation and browser clock skew cannot revoke a session.
          if (mounted) {
            clearTimeout(timer);
            timer = setTimeout(verifyDeadline, 30000);
          }
        });
      }, delay);
    };
    const sync = () => {
      const current = getStoredAuthIdentity();
      const currentToken = getStoredAuthToken();
      if (current !== identity || currentToken !== token) {
        identity = current;
        token = currentToken;
        if (current) dispatch(checkToken());
        else dispatch({ type: SIGN_OUT });
      }
      verifyDeadline();
    };
    const recover = (event) => {
      dispatch({ type: SIGN_OUT });
      const path = history.location.pathname;
      if (!/^\/(manage|client)(\/|$)/.test(path)) return;
      const portal = event.detail.role === "CLIENT" || path.startsWith("/client") ? "/client" : "/manage";
      // Let the protected form unmount (including its unsaved-change Prompt) before navigating.
      redirectTimer = setTimeout(() => history.replace(portal), 0);
    };
    const sharedRecovery = (event) => {
      if (event.key === "token" && !event.newValue && localStorage.getItem("eeAuthRecoveryReason")) {
        sessionStorage.setItem("eeLoginReason", localStorage.getItem("eeAuthRecoveryReason"));
        window.dispatchEvent(new CustomEvent(AUTH_RECOVERY_EVENT, { detail: {} }));
      }
    };
    const activity = (event) => {
      if (event.isTrusted) reportUserActivity().catch(() => undefined);
    };
    const connection = () => setWarning(getSessionWarning());
    const online = () => {
      if (getPendingLogout()) retryPendingLogout().catch(() => undefined);
      else if (getStoredAuthSession()) refreshAccessToken().catch(() => undefined);
    };
    const unsubscribe = subscribeAuthTokenChanges(sync);
    window.addEventListener(AUTH_RECOVERY_EVENT, recover);
    window.addEventListener("storage", sharedRecovery);
    window.addEventListener(SESSION_WARNING_EVENT, connection);
    window.addEventListener("online", online);
    ["pointerdown", "keydown", "scroll", "touchstart"].forEach(name => document.addEventListener(name, activity, { capture: true, passive: true }));
    sync();
    if (getPendingLogout()) retryPendingLogout().catch(() => undefined);
    return () => {
      mounted = false;
      clearTimeout(timer);
      clearTimeout(redirectTimer);
      unsubscribe();
      window.removeEventListener(AUTH_RECOVERY_EVENT, recover);
      window.removeEventListener("storage", sharedRecovery);
      window.removeEventListener(SESSION_WARNING_EVENT, connection);
      window.removeEventListener("online", online);
      ["pointerdown", "keydown", "scroll", "touchstart"].forEach(name => document.removeEventListener(name, activity, true));
    };
  }, [dispatch]);
  return warning && (getStoredAuthToken() || getPendingLogout()) ? (
    <div role="status" className="alert alert-warning position-fixed mb-0" style={{ bottom: 0, left: 0, right: 0, zIndex: 2000 }}>
      {t(getPendingLogout() ? "auth:sessionLogoutPending" : "auth:sessionUnavailable")}
      <button
        type="button"
        className="btn manage-teal-button ml-2"
        onClick={() => (getPendingLogout() ? retryPendingLogout() : refreshAccessToken()).catch(() => undefined)}
      >{t("auth:retrySession")}
      </button>
    </div>
  ) : null;
}
